import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  ICategoriaUbicacion,
  ICreateUbicacion,
  IGeoJSONCircle,
  IGeoJSONPoint,
  IUbicacion,
  IUpdateUbicacion,
} from 'modelos/src';
import { firstValueFrom } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { UbicacionService } from '../../../../auxiliares/servicios/http/ubicacion.service';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { AuxiliaresModule } from '../../../../auxiliares/auxiliares.module';
import { MapDrawMapboxComponent } from '../../../../auxiliares/map-draw-mapbox/map-draw-mapbox.component';
import { MatDialogRef } from '@angular/material/dialog';
import { ParamsService } from '../../../../auxiliares/servicios/params.service';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';

@Component({
  imports: [AuxiliaresModule, MapDrawMapboxComponent],
  selector: 'app-crear-editar-ubicacion',
  templateUrl: './crear-editar-ubicacion.component.html',
  styleUrl: './crear-editar-ubicacion.component.scss',
})
export class CrearEditarUbicacionComponent implements OnInit {
  dialogRef = inject<MatDialogRef<CrearEditarUbicacionComponent>>(
    MatDialogRef,
    { optional: true },
  );
  private service = inject(UbicacionService);
  private route = inject(ActivatedRoute);
  private lista2 = inject(Lista2Service);
  helper = inject(HelperService);
  private paramsService = inject(ParamsService);

  public loading = false;
  public title?: string;
  public subtitle?: string;
  public categoria?: ICategoriaUbicacion;
  public form?: FormGroup;
  public id?: string | null;
  public ubicacion?: IUbicacion;

  public centrarA?: IGeoJSONCircle | IGeoJSONPoint;
  /** Qué dibuja el mapa; 'Circle' se persiste como Point + radius */
  public tipoDibujo:
    | 'Point'
    | 'LineString'
    | 'Polygon'
    | 'Circle'
    | 'MultiPolygon' = 'Polygon';

  get geojson() {
    return this.form?.get('geojson') as FormGroup;
  }

  private createForm() {
    let type: 'Point' | 'LineString' | 'Polygon' | 'Circle' | 'MultiPolygon';
    if (this.ubicacion) {
      // Editando
      if ((this.ubicacion.geojson as IGeoJSONCircle).radius) {
        type = 'Circle';
      } else {
        type = this.ubicacion?.geojson?.type;
      }
    } else {
      // Creando
      switch (this.categoria) {
        case 'Terminal':
          type = 'Polygon';
          break;
        case 'Domicilio':
          type = 'Point';
          break;
        case 'Vehiculos':
          type = 'Polygon';
          break;
        case 'Luminarias':
          type = 'Polygon';
          break;
        case 'Alarmas':
          type = 'Polygon';
          break;
      }
    }

    this.tipoDibujo = type ?? 'Polygon';

    this.form = new FormGroup({
      identificacion: new FormControl(this.ubicacion?.identificacion, [
        Validators.required,
      ]),
      categoria: new FormControl(this.categoria, [Validators.required]),
      direccion: new FormControl(this.ubicacion?.direccion),
      color: new FormControl(this.ubicacion?.color || '#ffcc33', [
        Validators.required,
      ]),
      geojson: new FormGroup({
        // 'Circle' no es un tipo GeoJSON válido: se persiste Point + radius
        type: new FormControl(type === 'Circle' ? 'Point' : type, [
          Validators.required,
        ]),
        coordinates: new FormControl(this.ubicacion?.geojson?.coordinates, [
          Validators.required,
        ]),
        radius: new FormControl(
          (this.ubicacion?.geojson as IGeoJSONCircle)?.radius ?? null,
        ),
      }),
    });
  }

  private async listar() {
    if (this.id) {
      this.ubicacion = await firstValueFrom(this.lista2.ubicacion(this.id));
    }
  }

  public async onSubmit() {
    this.loading = true;
    try {
      let res = null;
      if (this.id) {
        // Update
        const data = this.getData();
        await this.service.update(this.id, data as IUpdateUbicacion);
        this.helper.notifSuccess('Actualizado correctamente');
      } else {
        // Create
        const data = this.getData();

        res = await this.service.create(data);
        this.helper.notifSuccess('Creado correctamente');
      }
      this.volver(res?._id);
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    } finally {
      this.loading = false;
    }
  }

  public volver(res?: string) {
    if (this.dialogRef) {
      this.dialogRef.close(res);
    } else {
      HelperService.volver();
    }
  }

  public getData(): ICreateUbicacion | IUpdateUbicacion {
    const data: ICreateUbicacion | IUpdateUbicacion = this.form?.value;
    // El radio solo aplica a círculos: no mandar null en el resto
    if (data?.geojson && (data.geojson as IGeoJSONCircle).radius == null) {
      delete (data.geojson as IGeoJSONCircle).radius;
    }
    return data;
  }

  private setTitles() {
    if (this.ubicacion) {
      this.title = 'Editar Ubicación';
      this.subtitle = this.ubicacion?.identificacion;
    } else {
      this.title = 'Crear Ubicación';
    }
  }

  async ngOnInit() {
    // getParams() es null si nunca se seteó (ej: navegación directa por URL)
    const params = this.paramsService.getParams();
    if (params?.['categoria']) {
      this.categoria = params['categoria'] as ICategoriaUbicacion;
      this.ubicacion = params['ubicacion'];
      this.id = this.ubicacion?._id;
    } else {
      const routeParams = await firstValueFrom(this.route.params);
      this.categoria = routeParams?.['categoria'] as ICategoriaUbicacion;
      this.id = routeParams?.['id'];
    }
    // Cargar la ubicación antes de crear el form: si no, al editar por URL
    // el form queda vacío
    if (!this.ubicacion) await this.listar();
    this.setTitles();
    this.createForm();
  }

  public onColorChange(event: any, controlName: string) {
    this.form?.get(controlName)?.setValue(event);
  }
}
